import { useEffect } from 'react';

/**
 * Keyboard zoom and pan for the fullscreen landmark editor (S4.3).
 *
 * Scope is deliberately narrow. An earlier version of this hook also bound Space/Enter to the
 * shutter and Escape to stop the camera; both were removed as speculative:
 *
 * - The **countdown** setting already covers hands-free capture, and covers it better — at navel
 *   height 2 m away you cannot reach a keyboard any more than a mouse.
 * - Keyboard capture does not make the flow keyboard-completable anyway, because landmark editing
 *   on the canvas is still pointer-only (see PLAN.md S4.2 — that needs a selection model nobody has
 *   designed yet). So it bought no WCAG 2.1.1 claim either.
 * - A global `keydown` listener live whenever the camera runs is real surface for hijacking a key
 *   someone needed. Not worth it for zero demonstrated demand.
 *
 * What survives has an actual user doing an actual task: the fullscreen editor *is* the
 * landmark-correction tool, and that is precision work. Holding a trackpad pinch steady while
 * judging whether a marker sits on the ASIS is worse than tapping `+`. With landmark misalignment
 * under active investigation, practitioners are about to do more of this, not less.
 *
 * The listener attaches only while the editor is open, so its blast radius is that dialog. Every
 * callback routes into the same `applyFullscreenViewport` seam the pointer handlers use — no new
 * geometry, nothing that can change what an analysis reports.
 */

export interface FullscreenEditorKeyboardOptions {
  /** Editor is open — the listener attaches only while this is true. */
  isFullScreenOpen: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** Pan by a pixel delta. Shift gives the coarse step. */
  onPan: (delta: { dx: number; dy: number }) => void;
  /** Escape hatch for tests. */
  enabled?: boolean;
}

/** Pan step in CSS pixels; Shift gives the coarse step. */
export const FULLSCREEN_PAN_STEP_PX = 24;
export const FULLSCREEN_PAN_STEP_COARSE_PX = 96;

const TEXT_ENTRY_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

export function useFullscreenEditorKeyboard({
  isFullScreenOpen,
  onZoomIn,
  onZoomOut,
  onPan,
  enabled = true,
}: FullscreenEditorKeyboardOptions): void {
  useEffect(() => {
    if (!enabled || !isFullScreenOpen) return;

    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      // Never hijack typing — a field inside the editor owns its own arrow keys.
      if (event.target instanceof Element && event.target.closest(TEXT_ENTRY_SELECTOR)) return;

      const step = event.shiftKey ? FULLSCREEN_PAN_STEP_COARSE_PX : FULLSCREEN_PAN_STEP_PX;

      switch (event.key) {
        case '+':
        case '=':
          event.preventDefault();
          onZoomIn();
          return;
        case '-':
        case '_':
          event.preventDefault();
          onZoomOut();
          return;
        case 'ArrowLeft':
          event.preventDefault();
          onPan({ dx: step, dy: 0 });
          return;
        case 'ArrowRight':
          event.preventDefault();
          onPan({ dx: -step, dy: 0 });
          return;
        case 'ArrowUp':
          event.preventDefault();
          onPan({ dx: 0, dy: step });
          return;
        case 'ArrowDown':
          event.preventDefault();
          onPan({ dx: 0, dy: -step });
          return;
        default:
          // Escape is left to the dialog, which closes itself.
          return;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, isFullScreenOpen, onZoomIn, onZoomOut, onPan]);
}
